import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { devError } from "@/lib/devLog";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateFR, findCategory, REMINDERS } from "@/lib/constants";
import { buildEventJsonLd, buildBreadcrumbJsonLd } from "@/lib/seo";
import SEO from "@/components/SEO";
import { track as trackAnalytics } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle2, MessageCircle, Calendar as CalIcon, MapPin, Phone, ExternalLink, Users, Send, Trophy, Image as ImageIcon, Trash2, Edit, Share2, Lock, Power, FileText } from "lucide-react";
import ReportEventButton from "@/components/ReportEventButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { b64ToSrc } from "@/lib/img";
import { MEAL_DIETS } from "@/lib/constants";
import RichText from "@/components/RichText";
import StoryShareDialog from "@/components/StoryShareDialog";
import PublisherCard from "@/components/PublisherCard";
import FlyerLightbox from "@/components/FlyerLightbox";

const CHAT_POLL_MS = 4000;

export default function EventDetail() {
    const { id } = useParams();
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [ev, setEv] = useState(null);
    const [comments, setComments] = useState([]);
    const [chat, setChat] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [chatMsg, setChatMsg] = useState("");
    const [commentText, setCommentText] = useState("");
    const [activateExchanges, setActivateExchanges] = useState(false);
    const [flyerOpen, setFlyerOpen] = useState(false);
    const [addAgenda, setAddAgenda] = useState(false);
    const [reminder, setReminder] = useState("");
    const [activeTab, setActiveTab] = useState("info");
    const [storyOpen, setStoryOpen] = useState(false);
    const [inviteState, setInviteState] = useState(null);
    const chatEndRef = useRef(null);
    const lastChatTsRef = useRef(null);

    const exchangesOn = !!ev?.my_participation?.exchanges_active;
    const myStatus = ev?.my_participation?.status;
    const canChat = myStatus === "joined" && exchangesOn;

    const load = async () => {
        try {
            const r = await api.get(`/events/${id}`);
            setEv(r.data);
            const cs = await api.get(`/events/${id}/comments`);
            setComments(cs.data);
            // Load chat invitation state for any joined participant — it
            // tells us whether someone has opened a chat ("Accept / Refuse"
            // banner) and whether the current user is an initiator.
            if (r.data.my_participation?.status === "joined") {
                try {
                    const inv = await api.get(`/events/${id}/chat/invitation`);
                    setInviteState(inv.data);
                } catch { setInviteState(null); }
            } else {
                setInviteState(null);
            }
            if (r.data.my_participation?.status === "joined" && r.data.my_participation?.exchanges_active) {
                try {
                    const ch = await api.get(`/events/${id}/chat`);
                    setChat(ch.data);
                    if (ch.data.length) lastChatTsRef.current = ch.data[ch.data.length - 1].created_at;
                    const p = await api.get(`/events/${id}/participants`);
                    setParticipants(p.data);
                } catch (err) {
                    // Chat/participants access can fail when exchanges are
                    // toggled off or server hiccups — safe to ignore silently.
                    if (process.env.NODE_ENV === "development") {
                        console.debug("chat/participants load skipped", err?.message);
                    }
                }
            } else {
                setChat([]);
                setParticipants([]);
                lastChatTsRef.current = null;
            }
        } catch { toast.error("Événement introuvable"); }
    };
    useEffect(() => {
        // Always start the detail view at the top of the page — when a
        // user clicks an event card from the home (especially after
        // scrolling far down), without this we'd land on whatever scrollY
        // was set, hiding the flyer.
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        // Gate: visiteurs non connectés -> redirection inscription
        if (authLoading) return;
        if (!user) {
            toast.info("Inscris-toi pour profiter des événements publiés par la communauté discoveuriste");
            nav("/register");
            return;
        }
        load();
        /* eslint-disable-next-line */
    }, [id, user, authLoading]);

    // Polling chat: only when chat tab is active and exchanges enabled
    useEffect(() => {
        if (!canChat || activeTab !== "chat") return undefined;
        let stopped = false;
        const tick = async () => {
            try {
                const since = lastChatTsRef.current;
                const url = since
                    ? `/events/${id}/chat?since=${encodeURIComponent(since)}`
                    : `/events/${id}/chat`;
                const r = await api.get(url);
                if (stopped) return;
                if (since) {
                    if (r.data.length) {
                        setChat((prev) => [...prev, ...r.data]);
                        lastChatTsRef.current = r.data[r.data.length - 1].created_at;
                    }
                } else {
                    setChat(r.data);
                    if (r.data.length) lastChatTsRef.current = r.data[r.data.length - 1].created_at;
                }
            } catch (err) {
                // Polling errors are expected (tab inactive, network blip).
                // Silent by design to avoid flooding the user with toasts.
                if (process.env.NODE_ENV === "development") {
                    console.debug("chat polling skipped", err?.message);
                }
            }
        };
        const itv = setInterval(tick, CHAT_POLL_MS);
        return () => { stopped = true; clearInterval(itv); };
    }, [canChat, activeTab, id]);

    // Auto scroll on new chat messages
    useEffect(() => {
        if (activeTab === "chat" && chatEndRef.current) {
            chatEndRef.current.scrollTop = chatEndRef.current.scrollHeight;
        }
    }, [chat.length, activeTab]);

    if (!ev) return <div className="p-10 text-center">Chargement...</div>;
    const cat = findCategory(ev.category);
    const Icon = cat?.icon;
    const title = ev.title || ev.restaurant_name;
    const isMine = user && ev.owner_id === user.id;
    const isAdmin = user?.role === "admin";
    const canManage = isMine || isAdmin;

    const follow = async () => {
        try {
            await api.post(`/events/${id}/follow`);
            try { trackAnalytics("event_follow", { event_id: id, city: ev?.city, category: ev?.category }); } catch (err) { devError("trackAnalytics event_follow failed", err); }
            toast.success("Suivi"); load();
        }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const join = async () => {
        try {
            await api.post(`/events/${id}/join`, {
                activate_exchanges: activateExchanges,
                add_to_agenda: addAgenda,
                reminder: reminder || null,
            });
            try { trackAnalytics("event_join", { event_id: id, city: ev?.city, category: ev?.category, exchanges: activateExchanges }); } catch (err) { devError("trackAnalytics event_join failed", err); }
            toast.success(activateExchanges ? "Rejoint ! Échanges activés." : "Rejoint !");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const leave = async () => {
        await api.delete(`/events/${id}/participation`);
        toast.success("Vous avez quitte cet événement"); load();
    };
    const toggleExchanges = async (on) => {
        try {
            await api.patch(`/events/${id}/participation/exchanges`,
                { activate_exchanges: on });
            toast.success(on ? "Échanges activés — vous pouvez discuter."
                              : "Échanges désactivés — vous n'êtes plus joignable.");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const openChat = async () => {
        try {
            await api.post(`/events/${id}/chat/open`);
            toast.success("Tu as ouvert le tchat — les autres participants peuvent te rejoindre.");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const respondInvite = async (response) => {
        try {
            await api.post(`/events/${id}/chat/invite-response`, { response });
            toast.success(response === "yes"
                ? "Tu as rejoint le tchat."
                : "Invitation refusée. Tu peux changer d'avis à tout moment.");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const sendChat = async () => {
        if (!chatMsg.trim()) return;
        try {
            const r = await api.post(`/events/${id}/chat`, { content: chatMsg });
            setChatMsg("");
            // Optimistic append + advance cursor so polling won't duplicate
            setChat((prev) => [...prev, r.data]);
            lastChatTsRef.current = r.data.created_at;
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const deleteChatMsg = async (mid) => {
        try {
            await api.delete(`/events/${id}/chat/${mid}`);
            setChat((prev) => prev.map((m) => m.id === mid ? { ...m, deleted: true, content: "[Message supprimé]" } : m));
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const sendComment = async () => {
        if (!commentText.trim()) return;
        try {
            await api.post(`/events/${id}/comments`, { content: commentText });
            setCommentText(""); load();
            toast.success("Commentaire ajoute (+1 point)");
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const remove = async () => {
        if (!window.confirm("Supprimer cet événement ?")) return;
        await api.delete(`/events/${id}`);
        toast.success("Supprimé");
        nav("/");
    };

    const shareEvent = async () => {  // kept for potential plain-link copy
        const url = `${window.location.origin}/events/${id}`;
        try { await navigator.clipboard.writeText(url); } catch (err) {
            if (process.env.NODE_ENV === "development") console.warn("clipboard", err);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
            {(() => {
                const dateStr = ev.date ? formatDateFR(ev.date) : "";
                const cityStr = ev.city ? ` — ${ev.city}` : "";
                const name = ev.title || ev.restaurant_name || "Événement";
                return (
                    <SEO
                        title={`${name}${cityStr}${dateStr ? ` · ${dateStr}` : ""} | Discoveur`}
                        description={(ev.description || `Événement ${name} ${cityStr} le ${dateStr}. Découvrez les détails, le lieu et le tarif sur Discoveur.`).slice(0, 300)}
                        canonical={`https://discoveur.com/events/${ev.id}`}
                        image={ev.flyer_url ? `https://discoveur.com${ev.flyer_url}` : (ev.flyer_base64 ? `https://discoveur.com/api/events/${ev.id}/flyer.jpg` : undefined)}
                        type="article"
                        jsonLd={[
                            buildEventJsonLd(ev),
                            buildBreadcrumbJsonLd([
                                { name: "Accueil", path: "/" },
                                { name: ev.city || "Événements", path: ev.city ? `/?city=${encodeURIComponent(ev.city)}` : "/" },
                                { name, path: `/events/${ev.id}` },
                            ]),
                        ].filter(Boolean)}
                    />
                );
            })()}
            <div className="discover-card overflow-hidden">
                <div className="relative bg-black/95 flex items-center justify-center"
                    style={{ minHeight: 220 }}>
                    {(ev.flyer_url || ev.flyer_base64) ? (
                        <button type="button"
                            onClick={() => setFlyerOpen(true)}
                            className="group relative block w-full focus:outline-none focus:ring-4 focus:ring-primary/40"
                            data-testid="flyer-clickable"
                            aria-label="Agrandir le flyer en plein écran">
                            <img src={ev.flyer_url || b64ToSrc(ev.flyer_base64)} alt={title}
                                className={`w-full max-h-[80vh] object-contain ${ev.deleted ? "grayscale" : ""}`}
                                data-testid="event-flyer-img"
                                style={{ cursor: "zoom-in" }} />
                            {/* Desktop hint overlay — visible on hover only, encourages click-to-zoom */}
                            <span className="hidden md:flex pointer-events-none absolute inset-0 items-center justify-center
                                             opacity-0 group-hover:opacity-100 transition-opacity duration-200
                                             bg-black/30">
                                <span className="px-4 py-2 rounded-full bg-white/95 text-black text-sm font-semibold shadow-lg flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /><path d="M11 8v6" /><path d="M8 11h6" />
                                    </svg>
                                    Cliquer pour agrandir
                                </span>
                            </span>
                            {/* Always-on small badge on mobile so users know it's tappable */}
                            <span className="md:hidden pointer-events-none absolute bottom-3 right-3
                                             px-2.5 py-1 rounded-full bg-black/55 text-white text-[10px] font-semibold backdrop-blur-sm flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                                </svg>
                                Zoom
                            </span>
                        </button>
                    ) : (
                        <div className="w-full bg-discover-gradient flex items-center justify-center"
                            style={{ aspectRatio: "16/9" }}>
                            {Icon && <Icon size={120} className="text-white/80" strokeWidth={1.2} />}
                        </div>
                    )}
                    {ev.modifiéd_stamp && (
                        <span className="absolute top-4 right-4 px-3 py-1.5 text-xs font-bold rounded-full bg-amber-500 text-white shadow-lg">
                            MODIFIE
                        </span>
                    )}
                    {ev.deleted && (
                        <div className="absolute inset-0 bg-red-600/40 flex items-center justify-center">
                            <span className="px-6 py-3 rounded-full bg-red-700 text-white font-black text-2xl">SUPPRIME</span>
                        </div>
                    )}
                </div>
                <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            {cat && <span className="text-xs font-bold px-3 py-1 rounded-full bg-accent text-accent-foreground">{cat.label}</span>}
                            {ev.gratuit && <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">Gratuit</span>}
                            {ev.reservation_conseillee && <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-100 text-amber-700">Réservation conseillée</span>}
                        </div>
                        {canManage && !ev.deleted && (
                            <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="rounded-full" onClick={() => nav(`/publish?edit=${id}`)} data-testid="event-edit-btn"><Edit size={14} /></Button>
                                <Button size="sm" variant="destructive" className="rounded-full" onClick={remove} data-testid="event-delete-btn"><Trash2 size={14} /></Button>
                            </div>
                        )}
                    </div>
                    <h1 className="text-3xl font-black">{title}</h1>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><CalIcon size={14} />{formatDateFR(ev.date)}{ev.end_date ? ` -> ${formatDateFR(ev.end_date)}` : ""}</span>
                        {ev.heure_debut && <span>{ev.heure_debut}{ev.heure_fin ? ` - ${ev.heure_fin}` : ""}</span>}
                        <span className="flex items-center gap-1"><MapPin size={14} />{ev.city}, {ev.country}</span>
                        <span className="flex items-center gap-1"><Users size={14} />{ev.nb_participants} participant{ev.nb_participants > 1 ? "s" : ""}</span>
                    </div>
                    {!ev.publisher_card && (
                        <p className="text-sm text-muted-foreground">
                            🔒 Publication anonyme
                        </p>
                    )}

                    {!ev.deleted && (
                        <div className="flex flex-wrap gap-2 pt-2">
                            <button onClick={follow}
                                className={`px-5 py-2.5 rounded-full font-semibold flex items-center gap-2 ${myStatus === "followed" ? "bg-amber-100 text-amber-700" : "border border-border hover:bg-accent"}`}
                                data-testid="detail-follow-btn">
                                <Star size={16} className={myStatus === "followed" ? "fill-amber-500 text-amber-500" : ""} />
                                {myStatus === "followed" ? "Suivi" : "Suivre"}
                            </button>
                            <Dialog>
                                <DialogTrigger asChild>
                                    <button className={`px-5 py-2.5 rounded-full font-semibold flex items-center gap-2 ${myStatus === "joined" ? "bg-emerald-500 text-white" : "bg-discover-gradient text-white"}`}
                                        data-testid="detail-join-btn">
                                        <CheckCircle2 size={16} />
                                        {myStatus === "joined" ? "Rejoint" : "Rejoindre"}
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="rounded-3xl">
                                    <DialogHeader>
                                        <DialogTitle>{myStatus === "joined" ? "Modifiér ma participation" : "Rejoindre l'événement"}</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 pt-2">
                                        <label className="flex items-center justify-between cursor-pointer">
                                            <div>
                                                <div className="font-semibold text-sm">Activér les échanges</div>
                                                <div className="text-xs text-muted-foreground">Discuter avec les autres participants, covoiturage, point de rdv...</div>
                                            </div>
                                            <Switch checked={activateExchanges} onCheckedChange={setActivateExchanges} data-testid="join-exchanges-switch" />
                                        </label>
                                        <label className="flex items-center justify-between cursor-pointer">
                                            <div>
                                                <div className="font-semibold text-sm">Ajouter a mon agenda</div>
                                                <div className="text-xs text-muted-foreground">Avec rappel paramétrable</div>
                                            </div>
                                            <Switch checked={addAgenda} onCheckedChange={setAddAgenda} data-testid="join-agenda-switch" />
                                        </label>
                                        {addAgenda && (
                                            <Select value={reminder} onValueChange={setReminder}>
                                                <SelectTrigger className="rounded-2xl" data-testid="join-reminder"><SelectValue placeholder="Rappel..." /></SelectTrigger>
                                                <SelectContent>
                                                    {REMINDERS.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        )}
                                        <Button onClick={join} className="w-full rounded-full bg-discover-gradient text-white" data-testid="join-confirm">
                                            Confirmer
                                        </Button>
                                        {myStatus === "joined" && (
                                            <Button onClick={leave} variant="outline" className="w-full rounded-full" data-testid="join-leave">
                                                Quitter cet événement
                                            </Button>
                                        )}
                                    </div>
                                </DialogContent>
                            </Dialog>
                            <button onClick={() => setStoryOpen(true)}
                                className="px-5 py-2.5 rounded-full font-semibold flex items-center gap-2 border border-border hover:bg-accent"
                                data-testid="detail-share-btn">
                                <Share2 size={16} />Partager
                            </button>
                            {user && user.id !== ev.owner_id && (
                                <ReportEventButton eventId={id} variant="ghost"
                                    className="text-muted-foreground" />
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="rounded-full bg-muted">
                    <TabsTrigger value="info" className="rounded-full" data-testid="tab-info">Infos</TabsTrigger>
                    {ev.mode === "restaurant" && <TabsTrigger value="menu" className="rounded-full" data-testid="tab-menu">Menus</TabsTrigger>}
                    <TabsTrigger value="comments" className="rounded-full" data-testid="tab-comments">Commentaires ({comments.length})</TabsTrigger>
                    {myStatus === "joined" && (
                        <>
                            <TabsTrigger value="chat" className="rounded-full" data-testid="tab-chat">
                                <MessageCircle size={14} className="mr-1" />Discussion{!exchangesOn && <Lock size={11} className="ml-1 opacity-60" />}
                            </TabsTrigger>
                            {exchangesOn && <TabsTrigger value="people" className="rounded-full" data-testid="tab-people">Participants</TabsTrigger>}
                        </>
                    )}
                </TabsList>
                <TabsContent value="info" className="space-y-3 pt-4">
                    {ev.publisher_card && <PublisherCard card={ev.publisher_card} />}
                    {ev.description && <div className="discover-card p-5"><RichText text={ev.description} /></div>}
                    {ev.mode === "restaurant" && ev.meal_diets && ev.meal_diets.length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="event-meal-diets">
                            {ev.meal_diets.map(d => {
                                const def = MEAL_DIETS.find(x => x.id === d);
                                return (
                                    <span key={d} className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center gap-1">
                                        <span>{def?.emoji || "•"}</span>{def?.label || d}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-3">
                        <InfoBox title="Lieu">
                            <div>{ev.address || ev.city}</div>
                            <div className="text-muted-foreground">{ev.city}, {ev.country}</div>
                        </InfoBox>
                        {ev.mode === "restaurant" && (ev.opening_time || ev.closing_time || (ev.meal_periods && ev.meal_periods.length) || ev.delivery_service) && (
                            <InfoBox title="Restaurant" testid="event-restaurant-info">
                                {(ev.opening_time || ev.closing_time) && (
                                    <div><b>Horaires :</b> {ev.opening_time || "?"}{ev.closing_time ? ` - ${ev.closing_time}` : ""}</div>
                                )}
                                {ev.meal_periods && ev.meal_periods.length > 0 && (
                                    <div><b>Service :</b> {ev.meal_periods.map(p => p[0].toUpperCase() + p.slice(1)).join(" · ")}</div>
                                )}
                                {ev.delivery_service && <div className="text-emerald-600 font-semibold">Livraison disponible</div>}
                            </InfoBox>
                        )}
                        {(ev.prix || ev.dress_code || ev.infos_pratiques) && (
                            <ExpandableInfoBox title="Pratique" testid="event-pratique-info">
                                {ev.prix && <div><b>Tarif :</b> {ev.prix}</div>}
                                {ev.dress_code && <div><b>Dress code :</b> {ev.dress_code}</div>}
                                {ev.infos_pratiques && <div className="text-muted-foreground mt-1"><RichText text={ev.infos_pratiques} /></div>}
                            </ExpandableInfoBox>
                        )}
                        {(ev.lien_reservation || ev.lien_internet || ev.telephone) && (
                            <InfoBox title="Liens">
                                {/* iter54 — Analytics: capture any link
                                    click inside the "Liens" box so the
                                    dashboard can report CTR to phone /
                                    website / route (booking) per event.
                                    Uses event delegation to avoid
                                    touching the RichText internals. */}
                                <div onClickCapture={(e) => {
                                    const a = e.target.closest && e.target.closest("a");
                                    if (!a) return;
                                    const href = a.getAttribute("href") || "";
                                    let kind = "event_click_website";
                                    if (href.startsWith("tel:")) kind = "event_click_phone";
                                    else if (/maps\.(google|apple)|waze|geo:/i.test(href)) kind = "event_click_route";
                                    try { trackAnalytics(kind, { event_id: id, href: href.slice(0, 200) }); } catch (err) { devError("trackAnalytics click failed", err); }
                                }}>
                                    {ev.telephone && <div className="flex items-center gap-1"><Phone size={12} />{ev.indicatif} {ev.telephone}</div>}
                                    {ev.lien_reservation && (
                                        <div><RichText text={ev.lien_reservation} /></div>
                                    )}
                                    {ev.lien_internet && (
                                        <div><RichText text={ev.lien_internet} /></div>
                                    )}
                                    {ev.reservation_conseillee && <div className="text-amber-600 font-semibold">Réservation conseillée</div>}
                                </div>
                            </InfoBox>
                        )}
                        {ev.comment && <InfoBox title="Conseil"><RichText text={ev.comment} /></InfoBox>}
                        {/* iter40 — Pro/Admin attachments (PDF/images) shown
                            as large tiles at the very bottom of the event
                            card. Click to enlarge (image in lightbox, PDF
                            opens in a new tab). */}
                        {Array.isArray(ev.attachments) && ev.attachments.length > 0 && (
                            <AttachmentsViewer attachments={ev.attachments}
                                eventTitle={ev.title || ev.restaurant_name} />
                        )}
                    </div>
                </TabsContent>

                {ev.mode === "restaurant" && (
                    <TabsContent value="menu" className="space-y-2 pt-4">
                        <div className="discover-card p-3 space-y-2">
                            {Object.entries(ev.weekly_menus || {}).map(([day, info]) => (
                                <div key={day} className={`day-${day} rounded-2xl border-2 px-4 py-3 flex items-center justify-between gap-3`}>
                                    <div className="flex-1">
                                        <div className="font-bold capitalize">{day}</div>
                                        <div className="text-sm whitespace-pre-line">{info.menu || "—"}</div>
                                    </div>
                                    {info.price && <div className="font-black text-lg">{info.price}</div>}
                                </div>
                            ))}
                        </div>
                        {ev.menu_photos && ev.menu_photos.length > 0 && (
                            <div className="discover-card p-3">
                                <div className="text-xs font-bold uppercase text-muted-foreground mb-2">Photos</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {ev.menu_photos.map((b64, i) => (
                                        <img key={(b64 || "").slice(0, 40) + i} src={b64ToSrc(b64)} alt={`menu-photo-${i}`}
                                            className="w-full h-48 object-cover rounded-2xl" />
                                    ))}
                                </div>
                            </div>
                        )}
                    </TabsContent>
                )}

                <TabsContent value="comments" className="space-y-3 pt-4">
                    {user && (
                        <div className="discover-card p-4 space-y-2">
                            <Textarea value={commentText} onChange={(e) => setCommentText(e.target.value)}
                                placeholder="Partage ton avis... (+1 point)" rows={2}
                                className="rounded-2xl" data-testid="comment-input" />
                            <Button onClick={sendComment} className="rounded-full bg-discover-gradient text-white" data-testid="comment-submit">
                                Publier le commentaire
                            </Button>
                        </div>
                    )}
                    {comments.length === 0 && <p className="text-muted-foreground text-center py-6">Aucun commentaire pour l'instant.</p>}
                    {comments.map(c => (
                        <div key={c.id} className="discover-card p-4">
                            <div className="flex items-center gap-2 text-xs">
                                <span className="font-bold">{c.user_pseudo}</span>
                                <span className="text-muted-foreground">{c.user_grade}</span>
                                <span className="ml-auto text-muted-foreground">{formatDateFR(c.created_at)}</span>
                            </div>
                            <p className="mt-2"><RichText text={c.content} /></p>
                            {c.photo_base64 && <img src={b64ToSrc(c.photo_base64)} alt="" className="mt-2 rounded-2xl max-h-64" />}
                        </div>
                    ))}
                </TabsContent>

                {myStatus === "joined" && (
                    <>
                        <TabsContent value="chat" className="pt-4 space-y-4">
                            {/* Invitation banner — shown when someone else opened the chat and
                                the current user has not yet accepted/declined OR has declined. */}
                            {!exchangesOn && inviteState?.inviters?.length > 0 && !inviteState?.is_inviter && (
                                <div className="discover-card p-5 border-2 border-sky-400/40 bg-sky-50/60"
                                     data-testid="chat-invite-banner">
                                    <div className="flex items-start gap-3">
                                        <div className="w-12 h-12 rounded-full bg-discover-gradient flex items-center justify-center shrink-0">
                                            <MessageCircle size={22} className="text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-bold mb-1">
                                                {inviteState.inviters[0].pseudo}
                                                {inviteState.inviters.length > 1
                                                    ? ` et ${inviteState.inviters.length - 1} autre${inviteState.inviters.length - 1 > 1 ? "s" : ""}`
                                                    : ""}
                                                {" "}souhaite{inviteState.inviters.length > 1 ? "nt" : ""} échanger
                                            </h3>
                                            <p className="text-sm text-muted-foreground">
                                                {inviteState.my_response === "no"
                                                    ? "Tu as décliné cette invitation. Tu peux toujours rejoindre le tchat tant que l'évènement est en cours."
                                                    : "Veux-tu rejoindre la conversation ? Si tu refuses, le tchat reste invisible pour toi."}
                                            </p>
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                <Button onClick={() => respondInvite("yes")}
                                                    className="rounded-full bg-discover-gradient text-white"
                                                    data-testid="invite-accept-btn">
                                                    Oui, je rejoins
                                                </Button>
                                                {inviteState.my_response !== "no" && (
                                                    <Button onClick={() => respondInvite("no")}
                                                        variant="outline" className="rounded-full"
                                                        data-testid="invite-decline-btn">
                                                        Non, pas pour l'instant
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!exchangesOn ? (
                                <div className="discover-card p-8 text-center space-y-4">
                                    <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                                        <Lock size={28} className="text-muted-foreground" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold mb-1">Discussion désactivée</h3>
                                        <p className="text-sm text-muted-foreground max-w-md mx-auto">
                                            Pour préserver votre confidentialité, vous n'êtes <b>joignable par personne</b> tant que les échanges ne sont pas activés.
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                        <Button onClick={() => toggleExchanges(true)}
                                            className="rounded-full bg-discover-gradient text-white"
                                            data-testid="enable-exchanges-btn">
                                            <Power size={16} className="mr-2" />Autoriser les échanges
                                        </Button>
                                        {/* "Ouvrir le tchat" — also activates exchanges but, in addition,
                                            announces a chat invitation to the other joined participants
                                            so they can choose to join or not. */}
                                        {inviteState?.can_initiate && !inviteState?.has_open_chat && (
                                            <Button onClick={openChat}
                                                variant="outline"
                                                className="rounded-full"
                                                data-testid="open-chat-invite-btn">
                                                <MessageCircle size={16} className="mr-2" />Ouvrir le tchat
                                            </Button>
                                        )}
                                    </div>
                                    {inviteState?.has_open_chat && !inviteState?.is_inviter && (
                                        <p className="text-xs text-muted-foreground italic">
                                            Une discussion est déjà ouverte. Tu peux rejoindre quand tu veux.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="discover-card p-8 text-center space-y-4" data-testid="chat-cta-block">
                                    <div className="w-16 h-16 mx-auto rounded-full bg-discover-gradient flex items-center justify-center">
                                        <MessageCircle size={28} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold mb-1">Tchat actif</h3>
                                        <p className="text-sm text-muted-foreground max-w-md mx-auto">
                                            Le tchat des participants est désormais accessible depuis le bouton <b>Tchat</b> du menu (en haut). Emojis, notes vidéo (2 min) et photos polaroid éphémères y sont disponibles.
                                        </p>
                                    </div>
                                    <Button onClick={() => nav(`/chat/${id}`)}
                                        className="rounded-full bg-discover-gradient text-white"
                                        data-testid="open-chat-btn">
                                        <MessageCircle size={16} className="mr-2" />Ouvrir le tchat
                                    </Button>
                                    <button onClick={() => toggleExchanges(false)}
                                        className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 mx-auto"
                                        data-testid="disable-exchanges-btn">
                                        <Power size={12} />Désactiver les échanges
                                    </button>
                                </div>
                            )}
                        </TabsContent>
                        {exchangesOn && (
                            <TabsContent value="people" className="pt-4">
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {participants.length === 0 && <p className="col-span-2 text-center text-muted-foreground">Personne n'a encore activé les échanges.</p>}
                                    {participants.map(p => (
                                        <div key={p.user_id} className="discover-card p-4 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-discover-gradient flex items-center justify-center text-white font-bold">
                                                {p.prenom?.[0]}
                                            </div>
                                            <div>
                                                <div className="font-bold">{p.pseudo} {p.is_pro && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">PRO</span>}</div>
                                                <div className="text-xs text-muted-foreground">{p.grade?.name} - {p.city}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-3 text-center">
                                    Idées de prolongation : covoiturage, point de rendez-vous, verre avant/après — utilisez la discussion !
                                </p>
                            </TabsContent>
                        )}
                    </>
                )}
            </Tabs>
            <StoryShareDialog event={ev} open={storyOpen} onOpenChange={setStoryOpen} />
            {flyerOpen && (ev.flyer_url || ev.flyer_base64) && (
                <FlyerLightbox
                    src={ev.flyer_url || b64ToSrc(ev.flyer_base64)}
                    alt={title}
                    onClose={() => setFlyerOpen(false)}
                />
            )}
        </div>
    );
}

function InfoBox({ title, children, testid }) {
    return (
        <div className="discover-card p-4" data-testid={testid}>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">{title}</div>
            <div className="space-y-0.5 text-sm">{children}</div>
        </div>
    );
}

/**
 * iter37 — Variant of InfoBox that becomes tap-to-expand on small screens
 * (< md). On mobile the card is initially clamped to a fixed max-height
 * and a "Voir plus / Voir moins" toggle reveals the full content with a
 * smooth height transition. Desktop renders the card unclamped (same
 * behaviour as the original InfoBox) since the wider layout already
 * shows everything.
 */
function ExpandableInfoBox({ title, children, testid }) {
    const [expanded, setExpanded] = useState(false);
    const contentRef = useRef(null);
    const [overflow, setOverflow] = useState(false);
    const CLAMP_PX = 96; // matches max-h-24 on mobile

    useEffect(() => {
        if (!contentRef.current) return;
        // Detect whether the natural content height exceeds the clamp
        // limit — we only show the toggle button when there's something
        // meaningful to expand.
        const el = contentRef.current;
        const measure = () => setOverflow(el.scrollHeight > CLAMP_PX + 4);
        measure();
        // Re-measure on viewport resize (orientation change, devtools, etc.)
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [children]);

    return (
        <div className="discover-card p-4" data-testid={testid || "expandable-info-box"}>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                {title}
            </div>
            <div
                ref={contentRef}
                className={`space-y-0.5 text-sm overflow-hidden transition-[max-height] duration-300 ease-out
                            md:max-h-none
                            ${expanded ? "max-h-[1200px]" : "max-h-24 md:max-h-none"}`}
                data-testid="expandable-info-content"
                data-expanded={expanded ? "true" : "false"}>
                {children}
            </div>
            {overflow && (
                <button type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="md:hidden mt-2 text-xs font-bold uppercase tracking-wider text-primary hover:underline"
                    data-testid="expandable-info-toggle"
                    aria-expanded={expanded}>
                    {expanded ? "Voir moins ▴" : "Voir plus ▾"}
                </button>
            )}
        </div>
    );
}


/**
 * iter40 — Display of attachments published by a Pro/Admin. Renders 1
 * or 2 large tiles (icon + filename). Tap behaviour:
 *   - image → open in the existing FlyerLightbox (full-screen zoom).
 *   - PDF   → open in a new browser tab via a data URL Blob (no upload,
 *             stays client-side and prints OK).
 */
function AttachmentsViewer({ attachments, eventTitle }) {
    const [lightboxSrc, setLightboxSrc] = useState(null);

    const openImage = (att) => {
        setLightboxSrc(`data:${att.mime};base64,${att.data_base64}`);
    };
    const openPdf = (att) => {
        try {
            const bin = atob(att.data_base64);
            const buf = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
            const blob = new Blob([buf], { type: att.mime || "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener,noreferrer");
            // Revoke later so the new tab has time to load.
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            toast.error("Ouverture du PDF impossible.");
        }
    };

    return (
        <div className="discover-card p-4 space-y-3" data-testid="event-attachments">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Pièces jointes
            </div>
            <div className="grid grid-cols-2 gap-3">
                {attachments.map((att, i) => {
                    const isPdf = att.mime === "application/pdf";
                    const dataUrl = `data:${att.mime};base64,${att.data_base64}`;
                    return (
                        <button key={att.name + i} type="button"
                            onClick={() => (isPdf ? openPdf(att) : openImage(att))}
                            className="group relative aspect-square rounded-2xl border-2 border-border bg-card overflow-hidden hover:border-primary transition flex flex-col"
                            data-testid={`event-attachment-${i}`}
                            aria-label={`Ouvrir ${att.name}`}>
                            {isPdf ? (
                                <div className="flex-1 flex flex-col items-center justify-center gap-2 bg-rose-50 dark:bg-rose-900/30">
                                    <FileText size={56} className="text-rose-600 dark:text-rose-300 group-hover:scale-110 transition" />
                                    <span className="text-xs font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">
                                        Voir le PDF
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <img src={dataUrl} alt={att.name}
                                        className="flex-1 w-full h-full object-cover group-hover:scale-105 transition" />
                                    <div className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5">
                                        <ImageIcon size={14} />
                                    </div>
                                </>
                            )}
                            <div className="px-2 py-1.5 text-xs font-semibold truncate bg-card border-t"
                                title={att.name}>
                                {att.name}
                            </div>
                        </button>
                    );
                })}
            </div>
            {lightboxSrc && (
                <FlyerLightbox src={lightboxSrc}
                    alt={`${eventTitle || "Pièce jointe"}`}
                    onClose={() => setLightboxSrc(null)} />
            )}
        </div>
    );
}
